// Dependencies
import { IBoltTaskTemplate, useBoltApiClient } from "@bolt/core";
import {
  dialogStore,
  PrimaryButton,
  PrimaryGrid,
  PrimaryGridModule,
  utils,
  fieldTypes,
} from "@ucl/library";
import { useEffect, useRef } from "react";
import { TaskTemplateGridConfig } from "./config/TaskTemplateGridConfig";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { CreateBoltTaskTemplateForm } from "../TaskTemplateForm/CreateTaskTemplateForm";
import { RichTextEditorsOutputTextTypes } from "../../types/globalTypes";
import { EditBoltTaskTemplateForm } from "../TaskTemplateForm/EditTaskTemplateForm";

interface BoltTaskTemplateGridProps {
  apiBaseUrl: string;
  gridKey: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  taskViewsSettings?: Partial<{
    odataUrl: string;
    gridTitle: string;
    gridAutoName: string;
    shouldCreateAutoView: boolean;
    defaultGridViewTitle: string;
    showGridViewPicker: boolean;
    showUserDefaultView: boolean;
  }>;
  gridLastRefreshed?: number;
  entityTypeOptions?: fieldTypes.Option<string>[] | [];
}

export const BoltTaskTemplateGrid: React.FC<BoltTaskTemplateGridProps> = ({
  apiBaseUrl,
  gridKey,
  getAuthRequestHeader,
  taskViewsSettings,
  gridLastRefreshed,
  entityTypeOptions,
}) => {
  const primaryGridModule = useRef<PrimaryGridModule>(null);
  const boltApiClient = useBoltApiClient();
  useEffect(() => {
    if (gridLastRefreshed) {
      primaryGridModule.current?.getGridModule()?.getGridRef()?.refresh();
    }
  }, [gridLastRefreshed]);
  const refreshGrid = () => {
    primaryGridModule.current?.getGridModule()?.getGridRef()?.refresh();
  };

  //const handleRecordClick = (taskTemplate: IBoltTaskTemplate) => {
  // // Call optional external callback first
  // actions?.onRecordClick?.(taskTemplate);

  // // Open edit dialog
  // if (!taskTemplate.batchId) {
  //   console.error("Task template does not have a batchId");
  //   return;
  // }

  //   openEditTaskTemplateDialog({
  //     taskTemplateBatchId: taskTemplate.batchId,
  //     apiBaseUrl: apiBaseUrl,
  //     assigneeOdataUrl: `${apiBaseUrl}/odata/bolttaskassigneeodata`,
  //     getAuthRequestHeader: getAuthRequestHeader,
  //     onSave: () => {
  //       refreshGrid();
  //       dialogStore.closeDialog(dialogId);
  //     },
  //     onCancel: () => {
  //       dialogStore.closeDialog(dialogId);
  //     },
  //     // currentUser: { id: "default-user-id" },
  //     // actions: {
  //     //   onSubmitSuccessfully: async (
  //     //     updatedTaskTemplate: IBoltTaskTemplate | IBoltTaskTemplate[]
  //     //   ) => {
  //     //     actions?.onRecordUpdated?.(updatedTaskTemplate);
  //     //     refreshGrid();
  //     //   },
  //     // },
  //     // permissions: {
  //     //   hasEditPermission: true,
  //     // },
  //   });
  // };

  const dialogId = "add-bolt-task-template-dialog";
  const editDialogId = "edit-bolt-task-template-dialog";

  return (
    <section className="bolt-task-template-grid">
      <div className="bolt-task-template-grid-header">
        <PrimaryButton
          text="Add Task Template"
          iconProps={{
            icon: faPlus,
          }}
          onClick={() => {
            dialogStore.openDialog({
              id: dialogId,
              header: "Add Task Template",
              allowDragging: false,
              height: "800px",
              width: "1200px",
              content: () => (
                <CreateBoltTaskTemplateForm
                  apiBaseUrl={apiBaseUrl}
                  assigneeOdataUrl={`${apiBaseUrl}/odata/bolttaskassigneeodata`}
                  getAuthRequestHeader={getAuthRequestHeader}
                  richTextEditorsOutputTextType={
                    RichTextEditorsOutputTextTypes.HTML
                  }
                  entityTypeOptions={entityTypeOptions}
                  workflowType="Single Step"
                  showWorkflowTypeSelector={false}
                  onSave={() => {
                    refreshGrid();
                    dialogStore.closeDialog(dialogId);
                  }}
                  onCancel={() => {
                    dialogStore.closeDialog(dialogId);
                  }}
                />
              ),
              close: () => {
                dialogStore.closeDialog(dialogId);
              },
            });
          }}
        />
      </div>
      <div className="bolt-task-template-grid-content">
        <PrimaryGrid<IBoltTaskTemplate, {}>
          gridKey={gridKey || "bolt-task-template-grid"}
          ref={primaryGridModule}
          apiClient={boltApiClient}
          viewsSettings={{
            gridTitle: "Task Template",
            odataUrl: `${utils.strings.trimEndForwardSlashForURL(
              apiBaseUrl
            )}/bolt/userview/grid`,
            ...taskViewsSettings,
          }}
          odataUrl={`${utils.strings.trimEndForwardSlashForURL(
            apiBaseUrl
          )}/odata/bolttasktemplategridodata`}
          getAuthRequestHeader={getAuthRequestHeader}
          columnsConfiguration={TaskTemplateGridConfig}
          gridConfiguration={{
            allowExcelExport: true,
            onRecordClick: (ev) => {
              if (ev && ev.rowData) {
                dialogStore.openDialog({
                  id: editDialogId,
                  allowDragging: false,
                  header: "Edit Task Template",
                  height: "800px",
                  width: "1200px",
                  content: () => (
                    <EditBoltTaskTemplateForm
                      apiBaseUrl={apiBaseUrl}
                      assigneeOdataUrl={`${apiBaseUrl}/odata/bolttaskassigneeodata`}
                      getAuthRequestHeader={getAuthRequestHeader}
                      entityTypeOptions={entityTypeOptions}
                      taskTemplateBatchId={ev.rowData.batchId || ""}
                      richTextEditorsOutputTextType={
                        RichTextEditorsOutputTextTypes.HTML
                      }
                      onSave={() => {
                        refreshGrid();
                        dialogStore.closeDialog(editDialogId);
                        refreshGrid();
                      }}
                      onCancel={() => {
                        dialogStore.closeDialog(editDialogId);
                      }}
                    />
                  ),
                  close: () => {
                    dialogStore.closeDialog(editDialogId);
                  },
                });
              }
            },
          }}
          showGridFilters
        />
      </div>
    </section>
  );
};
